<template>
  <div
    v-if="pdfModal"
    class="absolute z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 !h-[calc(90vh)] !w-[80vw] max-w-[900px]"
    style="z-index: 200000"
  >
    <div class="relative h-full w-full">
      <div class="h-full w-full flex justify-center items-center">
        <iframe
          ref="pdfPreviewRef"
          :src="`${viewLink}`"
          :title="fileName"
          :width="width"
          :height="height"
          class="bg-white border-solid border-[#D8D8D8] w-full"
        />
      </div>

      <v-btn
        v-if="isGoogleDoc"
        class="absolute top-[14%] rounded-full right-[-2%]"
        color="primary"
        fab
        rounded
        :x-small="$vuetify.breakpoint.mdAndDown"
        :loading="isPrinting"
        @click="onPrint"
      >
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <v-btn
        class="absolute top-[6%] rounded-full right-[-2%]"
        color="primary"
        fab
        rounded
        :x-small="$vuetify.breakpoint.mdAndDown"
        :loading="isDownloading"
        @click="onDownload"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>

      <v-btn
        class="absolute top-[-2%] right-[-2%]"
        color="white"
        fab
        rounded
        :x-small="$vuetify.breakpoint.mdAndDown"
        @click="onClose"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { usePdfStore } from '@/store/modules/usePdfStore';
import { storeToRefs } from 'pinia';
import printJS from 'print-js';
import { downloadFile } from '@/helpers/download';
import axios from 'axios';

export default defineComponent({
  name: 'PdfPreview',
  props: {
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '100%',
    },
  },
  setup() {
    const pdfStore = usePdfStore();
    const { src, fileName, pdfModal } = storeToRefs(pdfStore);

    const isDownloading = ref(false);
    const isPrinting = ref(false);

    const viewLink = computed(() => {
      const srcValue = src.value as string;
      if (typeof srcValue === 'string' && srcValue.includes('docs.google.com')) {
        return srcValue.replace('view', 'preview').replace('edit', 'preview');
      }
      return srcValue;
    });

    const isGoogleDoc = computed(() => {
      return typeof src.value === 'string' && src.value.includes('docs.google.com');
    });

    function getGoogleDocExportLink(src: string) {
      return src.replace('edit', 'export').replace('view', 'export') + `&format=pdf&embedded=true`;
    }

    async function getGoogleDocBlobUrl(src: string) {
      const downloadLink = getGoogleDocExportLink(src);
      const response = await axios.get(downloadLink, { responseType: 'blob' });
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    }

    const onClose = () => pdfStore.closePreview();

    const onDownload = async () => {
      if (!src.value) {
        return;
      }

      try {
        isDownloading.value = true;

        if (!isGoogleDoc.value) {
          downloadFile(src.value, fileName.value);
          return;
        }

        const blobUrl = await getGoogleDocBlobUrl(src.value);
        downloadFile(blobUrl, fileName.value);
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        isDownloading.value = false;
      }
    };

    const onPrint = async () => {
      if (!src.value) {
        return;
      }

      if (!isGoogleDoc.value) {
        console.warn('Printing via blob fetch is only implemented for Google Docs currently.');
        return;
      }

      isPrinting.value = true;

      try {
        const blobUrl = await getGoogleDocBlobUrl(src.value);

        printJS({
          printable: blobUrl,
          type: 'pdf',
          onPrintDialogClose: () => {
            URL.revokeObjectURL(blobUrl);
            isPrinting.value = false;
          },
          onError: (error) => {
            console.error('Print.js failed:', error);
            URL.revokeObjectURL(blobUrl);
            isPrinting.value = false;
          },
        });
      } catch (error) {
        console.error('Failed to fetch document for printing:', error);
      } finally {
        isPrinting.value = false;
      }
    };

    const pdfPreviewRef = ref<HTMLIFrameElement>();

    function refreshGoogleDocIframe(iframeElement: HTMLIFrameElement) {
      if (!iframeElement) return;

      const originalWidth = iframeElement.style.width;
      const currentOffsetWidth = iframeElement.offsetWidth;

      iframeElement.style.width = currentOffsetWidth - 1 + 'px';

      setTimeout(() => {
        if (originalWidth) {
          iframeElement.style.width = originalWidth;
        } else {
          iframeElement.style.width = currentOffsetWidth + 'px';
        }
      }, 200);
    }

    watch(pdfPreviewRef, (newVal) => {
      if (newVal) {
        newVal.onload = () => {
          refreshGoogleDocIframe(newVal);
        };
      }
    });

    return {
      pdfPreviewRef,
      viewLink,
      src,
      fileName,
      pdfModal,

      onClose,
      onDownload,
      isGoogleDoc,
      isDownloading,
      onPrint,
      isPrinting,
    };
  },
});
</script>
